import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Clock, MapPin, Calendar, BookOpen, ChevronDown, ChevronUp, GraduationCap, Building2 } from 'lucide-react';
import api from '../components/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';

const JOURS_SEMAINE = [
  { id: 1, libelle: 'Lundi', color: 'bg-blue-50 border-blue-200', icon: '🌅' },
  { id: 2, libelle: 'Mardi', color: 'bg-purple-50 border-purple-200', icon: '🌤️' },
  { id: 3, libelle: 'Mercredi', color: 'bg-green-50 border-green-200', icon: '☀️' },
  { id: 4, libelle: 'Jeudi', color: 'bg-yellow-50 border-yellow-200', icon: '🌞' },
  { id: 5, libelle: 'Vendredi', color: 'bg-red-50 border-red-200', icon: '🌅' },
  { id: 6, libelle: 'Samedi', color: 'bg-indigo-50 border-indigo-200', icon: '🌤️' },
  { id: 7, libelle: 'Dimanche', color: 'bg-gray-50 border-gray-200', icon: '☀️' },
];

const CreateEmploisPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [semestres, setSemestres] = useState([]);
  const [salles, setSalles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedSemestre, setSelectedSemestre] = useState('');
  const [cours, setCours] = useState([]);
  const [expandedDay, setExpandedDay] = useState(1);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingSalles, setLoadingSalles] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fonction pour récupérer toutes les salles (gestion de la pagination)
        const fetchAllSalles = async () => {
          let allSalles = [];
          let page = 1;
          let hasMorePages = true;

          while (hasMorePages) {
            try {
              const response = await api.get(`/etablissement/salles/?page=${page}&page_size=100`);
              
              // Gestion de différentes structures de réponse API
              if (response.data && response.data.results && Array.isArray(response.data.results)) {
                // Structure standard DRF PageNumberPagination
                allSalles = [...allSalles, ...response.data.results];
                hasMorePages = !!response.data.next;
                page++;
              } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                // Structure personnalisée avec "data"
                allSalles = [...allSalles, ...response.data.data];
                const totalPages = response.data.pagination?.total_pages || 1;
                hasMorePages = page < totalPages;
                page++;
              } else if (Array.isArray(response.data)) {
                // Réponse directe sous forme de tableau (sans pagination)
                allSalles = [...allSalles, ...response.data];
                hasMorePages = false;
              } else {
                hasMorePages = false;
              }
            } catch (error) {
              console.error(`Erreur lors du chargement des salles:`, error);
              hasMorePages = false;
            }
          }

          return allSalles;
        };

        const [classesResponse, semestresResponse, allSalles] = await Promise.all([
          api.get('/classe/get-all-classe/'),
          api.get('/emplois/semestres/actifs/'),
          fetchAllSalles()
        ]);

        // S'assurer que les données sont toujours des tableaux et filtrer les salles actives
        setClasses(Array.isArray(classesResponse.data) ? classesResponse.data : []);
        setSemestres(Array.isArray(semestresResponse.data) ? semestresResponse.data : []);
        
        // Filtrer uniquement les salles actives (non archivées)
        const sallesActives = Array.isArray(allSalles) 
          ? allSalles.filter(salle => !salle.is_archive) 
          : [];
        setSalles(sallesActives);


      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        toast.error('Erreur lors du chargement des données');
        // S'assurer que les états restent des tableaux même en cas d'erreur
        setClasses([]);
        setSemestres([]);
        setSalles([]);
      } finally {
        setLoadingSalles(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchModules = async () => {
      if (selectedClasse) {
        setLoadingModules(true);
        try {
          const response = await api.get(`/emplois/modules-by-classe/${selectedClasse}/`);
          setModules(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
          console.error('Erreur lors du chargement des modules:', error);
          toast.error('Erreur lors du chargement des modules');
          setModules([]);
        } finally {
          setLoadingModules(false);
        }
      } else {
        setModules([]);
      }
    };

    fetchModules();
  }, [selectedClasse]);

  const handleAddCours = (jourId) => {
    const newCours = {
      id: Date.now(), // Utiliser un ID unique
      jour: jourId,
      heure_debut: '',
      heure_fin: '',
      module: '',
      salle: '',
    };
    setCours([...cours, newCours]);
  };

  const getCoursForDay = (jourId) => {
    return cours.filter(c => c.jour === jourId);
  };

  const handleRemoveCours = (id) => {
    setCours(cours.filter(c => c.id !== id));
  };

  const handleCoursChange = (id, field, value) => {
    setCours(cours.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClasse || !selectedSemestre) {
      toast.error('Veuillez sélectionner une classe et un semestre');
      return;
    }

    if (cours.length === 0) {
      toast.error('Veuillez ajouter au moins un cours');
      return;
    }

    // Vérifier que tous les cours ont des données valides
    const coursInvalides = cours.filter(c => 
      !c.module || !c.salle || !c.heure_debut || !c.heure_fin
    );
    
    if (coursInvalides.length > 0) {
      toast.error('Certains cours ont des informations manquantes');
      return;
    }

    setLoading(true);
    try {
      const anneeResponse = await api.get('/etablissement/annees-academiques/active/');
      
      if (!anneeResponse.data || !anneeResponse.data.id) {
        toast.error('Impossible de récupérer l\'année académique active');
        setLoading(false);
        return;
      }

      // S'assurer que les IDs sont des entiers valides
      const classeId = parseInt(selectedClasse);
      const semestreId = parseInt(selectedSemestre);
      const anneeId = parseInt(anneeResponse.data.id);
      
      if (isNaN(classeId) || classeId <= 0) {
        toast.error('Sélection de classe invalide');
        setLoading(false);
        return;
      }
      
      if (isNaN(semestreId) || semestreId <= 0) {
        toast.error('Sélection de semestre invalide');
        setLoading(false);
        return;
      }
      
      if (isNaN(anneeId) || anneeId <= 0) {
        toast.error('Année académique invalide');
        setLoading(false);
        return;
      }

      // Préparer les données pour l'API
      const emploiData = {
        classe_id: classeId,
        semestre_id: semestreId,
        annee_id: anneeId,
        cours: cours.map(({ id, ...rest }) => {
          // S'assurer que tous les IDs sont des entiers
          const moduleId = parseInt(rest.module);
          const salleId = parseInt(rest.salle);
          const jour = parseInt(rest.jour);
          
          // Vérifier que les conversions sont valides
          if (isNaN(moduleId) || isNaN(salleId) || isNaN(jour)) {
            throw new Error('Données de cours invalides');
          }
          
          // Formater les heures correctement
          let heure_debut = rest.heure_debut;
          let heure_fin = rest.heure_fin;
          
          // S'assurer que les heures sont au format correct HH:MM:SS
          if (heure_debut && !heure_debut.includes(':')) {
            heure_debut = `${heure_debut}:00`;
          } else if (heure_debut && heure_debut.split(':').length === 2) {
            heure_debut = `${heure_debut}:00`;
          }
          
          if (heure_fin && !heure_fin.includes(':')) {
            heure_fin = `${heure_fin}:00`;
          } else if (heure_fin && heure_fin.split(':').length === 2) {
            heure_fin = `${heure_fin}:00`;
          }
          
          return {
            jour,
            module: moduleId,
            salle: salleId,
            heure_debut,
            heure_fin
          };
        })
      };

      console.log('Données envoyées à l\'API:', JSON.stringify(emploiData, null, 2));
      
      const response = await api.post('/emplois/', emploiData);
      
      if (response.data) {
        toast.success('Emploi du temps créé avec succès');
        navigate('/liste-emplois');
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'emploi du temps:', error.response || error);
      
      // Meilleure gestion des erreurs pour éviter trop de messages
      let errorMessages = [];
      
      if (error.response?.data) {
        // Affichage des erreurs détaillées de l'API
        if (typeof error.response.data === 'object') {
          // Récupérer juste les messages d'erreur sans leur champ
          Object.entries(error.response.data).forEach(([field, message]) => {
            // Convertir tout en chaîne de caractères pour éviter les erreurs de rendu
            let errorMsg;
            if (Array.isArray(message)) {
              errorMsg = message[0];
            } else if (typeof message === 'object') {
              errorMsg = JSON.stringify(message);
            } else {
              errorMsg = String(message);
            }
            errorMessages.push(`${field}: ${errorMsg}`);
          });
        } else if (typeof error.response.data === 'string') {
          errorMessages.push(error.response.data);
        } else if (error.response.data.error) {
          errorMessages.push(String(error.response.data.error));
        }
      } else if (error.message) {
        // Erreur locale comme "Données de cours invalides"
        errorMessages.push(error.message);
      }
      
      // S'il n'y a pas de messages spécifiques, utiliser un message générique
      if (errorMessages.length === 0) {
        errorMessages.push('Erreur lors de la création de l\'emploi du temps');
      }
      
      // Afficher uniquement un toast avec le message principal
      if (errorMessages.length === 1) {
        toast.error(errorMessages[0], { autoClose: 6000 });
      } else {
        // Pour plusieurs messages, afficher une liste
        toast.error(
          <div>
            <strong>Erreur lors de la création de l'emploi du temps:</strong>
            <ul className="mt-2 list-disc pl-4">
              {errorMessages.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          </div>,
          { autoClose: 6000 }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#202654] to-[#2a2f6c]">
            <h1 className="text-2xl font-bold text-white">Créer un Emploi du Temps</h1>
            <p className="mt-1 text-sm text-gray-200">
              Créez un nouvel emploi du temps pour une classe
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <GraduationCap className="h-6 w-6 text-[#202654]" />
                  </div>
                  <label htmlFor="classe" className="block text-sm font-medium text-gray-700">
                    Classe
                  </label>
                </div>
                <select
                  id="classe"
                  value={selectedClasse}
                  onChange={(e) => setSelectedClasse(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                  required
                >
                  <option value="">Sélectionner une classe</option>
                  {Array.isArray(classes) && classes.map((classe) => (
                    <option key={classe.id} value={classe.id}>
                      {classe.libelle}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <BookOpen className="h-6 w-6 text-[#202654]" />
                  </div>
                  <label htmlFor="semestre" className="block text-sm font-medium text-gray-700">
                    Semestre
                  </label>
                </div>
                <select
                  id="semestre"
                  value={selectedSemestre}
                  onChange={(e) => setSelectedSemestre(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                  required
                >
                  <option value="">Sélectionner un semestre</option>
                  {Array.isArray(semestres) && semestres.map((semestre) => (
                    <option key={semestre.id} value={semestre.id}>
                      {semestre.libelle}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Planning Hebdomadaire</h2>
                <div className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-[#202654]" />
                  <span className="text-sm text-gray-600">
                    {selectedClasse ? `${Array.isArray(classes) ? classes.find(c => c.id === parseInt(selectedClasse))?.libelle || '' : ''}` : 'Sélectionnez une classe'}
                  </span>
                </div>
              </div>
              
              {JOURS_SEMAINE.map((jour) => (
                <div key={jour.id} className={`rounded-lg border ${jour.color} overflow-hidden shadow-sm hover:shadow-md transition-shadow`}>
                  <button
                    type="button"
                    onClick={() => setExpandedDay(expandedDay === jour.id ? null : jour.id)}
                    className="w-full px-6 py-4 flex justify-between items-center hover:bg-opacity-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{jour.icon}</span>
                      <h3 className="text-lg font-medium text-gray-900">{jour.libelle}</h3>
                    </div>
                    {expandedDay === jour.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>

                  {expandedDay === jour.id && (
                    <div className="p-6 bg-white">
                      <div className="space-y-4">
                        {getCoursForDay(jour.id).map((cours, index) => (
                          <div key={cours.id} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-center mb-4">
                              <div className="flex items-center space-x-2">
                                <div className="p-2 bg-[#202654]/10 rounded-lg">
                                  <Clock className="h-5 w-5 text-[#202654]" />
                                </div>
                                <h4 className="text-sm font-medium text-gray-900">Cours {index + 1}</h4>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveCours(cours.id)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1 hover:bg-red-50 rounded-full"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Heure début</label>
                                <div className="relative rounded-md shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Clock className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <input
                                    type="time"
                                    value={cours.heure_debut}
                                    onChange={(e) => handleCoursChange(cours.id, 'heure_debut', e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                    required
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Heure fin</label>
                                <div className="relative rounded-md shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Clock className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <input
                                    type="time"
                                    value={cours.heure_fin}
                                    onChange={(e) => handleCoursChange(cours.id, 'heure_fin', e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                    required
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Module</label>
                                <select
                                  value={cours.module}
                                  onChange={(e) => handleCoursChange(cours.id, 'module', e.target.value)}
                                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                  required
                                  disabled={loadingModules}
                                >
                                  <option value="">Sélectionner un module</option>
                                  {Array.isArray(modules) && modules.map((module) => (
                                    <option key={module.id} value={module.id}>
                                      {module.libelle}
                                    </option>
                                  ))}
                                </select>
                                {loadingModules && (
                                  <p className="mt-1 text-sm text-gray-500">Chargement des modules...</p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Salle</label>
                                <div className="relative rounded-md shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <select
                                    value={cours.salle}
                                    onChange={(e) => handleCoursChange(cours.id, 'salle', e.target.value)}
                                    className="block w-full pl-10 pr-3 py-2 border-gray-300 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm rounded-md"
                                    required
                                    disabled={loadingSalles}
                                  >
                                    <option value="">
                                      {loadingSalles ? 'Chargement des salles...' : 'Sélectionner une salle'}
                                    </option>
                                    {Array.isArray(salles) && salles.map((salle) => (
                                      <option key={salle.id} value={salle.id}>
                                        {salle.libelle} {salle.capacite ? `(${salle.capacite} places)` : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {loadingSalles && (
                                  <p className="mt-1 text-sm text-gray-500">Chargement des salles...</p>
                                )}
                                {!loadingSalles && salles.length === 0 && (
                                  <p className="mt-1 text-sm text-red-500">
                                    Aucune salle active disponible. Vérifiez la gestion des salles.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => handleAddCours(jour.id)}
                          className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Ajouter un cours pour {jour.libelle}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/liste-emplois')}
                className="inline-flex justify-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center px-6 py-3 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-colors"
              >
                {loading ? 'Création en cours...' : 'Créer l\'emploi du temps'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateEmploisPage;
